import React from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { RouteComponentProps, navigate, Redirect } from '@reach/router';
import { isOutPatientDomain, outpatientLoginRedirect } from 'api/Utils';
import {
  getCompanyManagers,
  getMyProviders,
  requireDocumentInFolder,
  requireProvider,
} from 'store/providers/providerSlice';
import { IProviders, ITags } from 'store/providers/providerTypes';
import { appDispatch, IAppInstance, RootState } from 'store/store';
import { display } from 'components/ErrorView/ErrorDialog';
import LogoLoading from 'components/LogoLoading/LogoLoading';
import { getRedirectToLoginLocation } from 'utils/happyFunctions';
import { addGuideToList } from 'store/siteGuide/SiteGuideSlice';
import {
  isCompanyAdmin,
  isCompanySupport,
} from 'store/companies/companiesUtils';
import { ICompany, TProjects } from 'store/companies/companiesTypes';
import {
  checkProviderStatus,
  IThisUserState,
} from 'store/thisUser/thisUserSlice';
import { IUser } from 'store/users/usersType';
import { getCompanyHandler } from 'store/companies/companiesSlice';
import { getAppInstance } from 'store/appInstance/appInstanceSlice';
import { changeStateOFSideBar } from 'store/app/appSlice';
import Icon from 'react-multi-date-picker/components/icon';

Icon;

interface IStateToProps {
  appInstance: IAppInstance;
  isSideBarCollapsed: boolean;
  managingCompany: ICompany;
  providers: IProviders;
  provider: IProviders['map'][0];
  thisUser: IThisUserState;
  isSupport: boolean;
  isAdmin: boolean;
  projects: TProjects;
  user: IUser;
  tags: ITags | null;
}

interface IDispatchToProps {
  changeStateOfSideBar: (isCollapsed: boolean) => any;
  checkProviderStatus: () => any;
  getAppInstance: () => any;
  getCompany: (companyId: number) => any;
  getMyProviders: () => any;
  requireProvider: (providerId: number) => any;
  getDocumentsInFolder: (folderId: number, providerId: number) => any;
  getCompanyManagers: (companyId: number) => any;
  addGuideToList: (key: string | string[]) => any;
}

interface IOwnProps {
  providerId?: string;
  folderId?: string;
  children?: React.ReactNode;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  loading: boolean;
  searching: boolean;
  isActiveGeneralSearch: boolean;
}

class ContentManagement extends React.Component<
  IComponentProps,
  IComponentStates
> {
  state = {
    loading: true,
    searching: false,
    isActiveGeneralSearch: false,
  };

  handleEnableGeneralSearch = (event: any) => {
    if (
      (event.ctrlKey && event.key === 'k') ||
      (event.metaKey && event.key === 'k')
    ) {
      this.handleChangeGeneralSearch(true);
    }
  };

  componentDidMount() {
    window.addEventListener('keydown', this.handleEnableGeneralSearch);

    this.props.addGuideToList(['manageDocuments', 'search-content']);
    const { providerId, thisUser } = this.props;
    const isLoggedIn = thisUser ? thisUser.isLoggedIn : false;
    if (isOutPatientDomain() && !isLoggedIn) {
      window.location.href = outpatientLoginRedirect;
    }
    if (isLoggedIn) {
      this.props.checkProviderStatus();
    }
    this.props.getAppInstance();
    const requests = [] as Promise<any>[];
    if (!this.props.provider) {
      requests.push(
        this.props
          .requireProvider(Number(providerId))
          .then(undefined, (err: any) => {
            if (err.code === 403)
              window.location.href = getRedirectToLoginLocation();
          })
      );
    }
    Promise.allSettled(requests)
      .then(() => {
        let companyId;
        const thisProvider = this.props.providers.map[providerId!];
        if (thisProvider) {
          companyId = thisProvider.data?.managingCompanyId;
          if (companyId && !this.props.managingCompany) {
            this.props.getCompany(companyId);
          }
        }
        this.setState({ loading: false });
      })
      .catch((err) => {
        if (err.code === 403)
          window.location.href = getRedirectToLoginLocation();
        display(err);
      });
  }

  componentDidUpdate(prevProps: IComponentProps) {
    const { providerId } = this.props;
    if (
      (prevProps === undefined || providerId !== prevProps.providerId) &&
      !this.props.provider
    ) {
      this.props.requireProvider(Number(providerId)).catch((err: any) => {
        display(err);
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleEnableGeneralSearch);
  }

  handleChangeRoute = (route: string) => () => {
    navigate(route);
  };

  handleCollapseSidebar = () => {
    this.props.changeStateOfSideBar(!this.props.isSideBarCollapsed);
  };

  handleChangeGeneralSearch = (status: boolean = false) => {
    this.setState({ isActiveGeneralSearch: status });
  };

  render() {
    const { children, providerId, providers, thisUser, isSupport } = this.props;

    const provider = providers.map[Number(providerId)];

    const isSelfProvider =
      provider && provider.data?.user === Number(thisUser.userId);
    if (
      this.props.folderId === undefined &&
      provider &&
      // @ts-expect-error it's ok
      provider.data?.userVirtualSpaces &&
      !isSelfProvider &&
      !isSupport
    )
      return <Redirect to="/homepage" noThrow={true} />;

    if (this.state.loading || !provider || provider.isLoading) {
      return <LogoLoading />;
    }

    return <>{children}</>;
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  ownProps
) => {
  const thisUser = state.thisUser;
  const appInstance = state.appInstance;
  const userId = Number(thisUser.userId);
  const user = state.users.map[userId];
  const providers = state.providers;
  const isSideBarCollapsed = state.app.UI.sidebar.isSideBarCollapsed;
  const provider = state.providers.map[Number(ownProps.providerId)];
  const companyId = provider?.data?.managingCompanyId;
  const company = state.companies.data[companyId!];
  const isSupport = company && isCompanySupport(thisUser, company);
  const isAdmin = company && isCompanyAdmin(thisUser, company);
  const tags = state.providers.tags[Number(ownProps.providerId)];
  const projects = company?.projects;

  return {
    managingCompany: company,
    appInstance,
    isSideBarCollapsed,
    providers,
    provider,
    thisUser,
    user,
    tags,
    isSupport,
    isAdmin,
    projects,
  };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch
) => ({
  getCompany: (companyId: number) =>
    dispatch(
      // @ts-expect-error it's ok
      getCompanyHandler({
        companyId,
        templateStub: null,
        companyStub: false,
        projectStub: false,
      })
    ),
  // @ts-expect-error it's ok
  getAppInstance: () => dispatch(getAppInstance({})),
  // @ts-expect-error it's ok
  getMyProviders: () => dispatch(getMyProviders({})),
  requireProvider: (providerId: number) =>
    appDispatch(requireProvider({ providerId })),
  // @ts-expect-error it's ok
  checkProviderStatus: () => dispatch(checkProviderStatus({})),
  changeStateOfSideBar: (isCollapsed: boolean) =>
    dispatch(changeStateOFSideBar({ isCollapsed })),
  getDocumentsInFolder: (folderId: number, providerId) =>
    // @ts-expect-error it's ok
    dispatch(requireDocumentInFolder({ folderId, providerId })),
  getCompanyManagers: (companyId: number) =>
    // @ts-expect-error it's ok
    dispatch(getCompanyManagers({ companyId })),
  addGuideToList: (key: string | string[]) => dispatch(addGuideToList(key)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentManagement);
